import { useState, useEffect } from 'react';
import { isMobileDevice, isRunningAsInstalledPwa } from '../utils/pwa';

/**
 * A lightweight version of the PWA debug panel for mobile devices
 * Minimizes network requests to avoid 502 errors
 */
const SimplePwaDebug = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [info, setInfo] = useState({
    hasManifest: false,
    manifestUrl: '',
    userAgent: '',
    isMobile: false,
    isInstalled: false
  });

  useEffect(() => {
    if (!isVisible) return;
    
    // Only gather the simplest information with no network requests
    const manifestLink = document.querySelector('link[rel="manifest"]');
    
    setInfo({
      hasManifest: !!manifestLink,
      manifestUrl: manifestLink ? manifestLink.href : 'None',
      userAgent: navigator.userAgent,
      isMobile: isMobileDevice(),
      isInstalled: isRunningAsInstalledPwa()
    });
  }, [isVisible]);

  // Toggle visibility
  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };
  
  // Add manifest link to head
  const addManifest = () => {
    if (!document.querySelector('link[rel="manifest"]')) {
      try {
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = '/manifest.webmanifest';
        document.head.appendChild(link);
        
        setInfo(prev => ({
          ...prev,
          hasManifest: true,
          manifestUrl: '/manifest.webmanifest'
        }));
        
        alert('Manifest link added successfully!');
      } catch (error) {
        alert('Error adding manifest: ' + error.message);
      }
    } else {
      alert('Manifest link already exists.');
    }
  };
  
  if (!isVisible) {
    return (
      <button 
        className="simple-pwa-toggle"
        onClick={toggleVisibility}
      >
        PWA Debug
      </button>
    );
  }
  
  return (
    <div className="simple-pwa-panel">
      <div className="simple-pwa-header">
        <h3>PWA Debug (Light)</h3>
        <button onClick={toggleVisibility}>Close</button>
      </div>
      
      <div className="simple-pwa-content">
        <div className="simple-pwa-info">
          <p><strong>Has Manifest:</strong> {info.hasManifest ? 'Yes' : 'No'}</p>
          <p><strong>Manifest URL:</strong> {info.manifestUrl}</p>
          <p><strong>Mobile Device:</strong> {info.isMobile ? 'Yes' : 'No'}</p>
          <p><strong>Installed as PWA:</strong> {info.isInstalled ? 'Yes' : 'No'}</p>
        </div>
        
        <div className="simple-pwa-actions">
          {!info.hasManifest && (
            <button 
              className="simple-pwa-btn" 
              onClick={addManifest}
            >
              Add Manifest Link
            </button>
          )}
          
          <a 
            href="/manifest.webmanifest"
            target="_blank"
            rel="noopener noreferrer"
            className="simple-pwa-btn simple-pwa-link"
          >
            View Manifest
          </a>
          
          <button 
            className="simple-pwa-btn"
            onClick={() => {
              localStorage.removeItem('pwa_prompt_dismissed');
              sessionStorage.removeItem('installBannerClosed');
              alert('Installation prompts reset!');
            }}
          >
            Reset Prompts
          </button>
        </div>
        
        <div className="simple-pwa-help">
          <p>If you're seeing 502 errors, the full debug panel might be causing issues.</p>
          <p>Try these steps:</p>
          <ol>
            <li>Ensure manifest.webmanifest exists</li>
            <li>Verify icons are accessible (logo192.png and logo512.png)</li>
            <li>Use Chrome on Android or Safari on iOS</li>
            <li>Clear application cache and try again</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default SimplePwaDebug; 